import { z, type ZodType } from 'zod';

import { assertOk, parseJson } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type JsonRequestOptions = {
  tenantSlug: string;
  signal?: AbortSignal;
};

async function requestJson<T>(
  path: string,
  schema: ZodType<T>,
  { tenantSlug, signal }: JsonRequestOptions,
): Promise<T> {
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(path, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      'X-Tenant': tenantSlug,
    },
  });

  await assertOk(response);
  return parseJson(response, schema);
}

const QrTrendPointSchema = z.object({
  day: z.string(),
  scans: z.number(),
  orders: z.number(),
});
export type QrTrendPoint = z.infer<typeof QrTrendPointSchema>;

const AovTrendPointSchema = z.object({
  day: z.string(),
  averageOrderValueCents: z.number(),
  orders: z.number(),
});
export type AovTrendPoint = z.infer<typeof AovTrendPointSchema>;

const ItemHeatmapEntrySchema = z.object({
  itemName: z.string(),
  hourly: z.array(z.number()),
  totalQuantity: z.number(),
});
export type ItemHeatmapEntry = z.infer<typeof ItemHeatmapEntrySchema>;

const GuestEngagementDailyPointSchema = z.object({
  day: z.string(),
  newGuests: z.number(),
  returningGuests: z.number(),
  loyaltyOrders: z.number(),
  loyaltyPointsAwarded: z.number(),
  loyaltyPointsRedeemed: z.number(),
});
export type GuestEngagementDailyPoint = z.infer<typeof GuestEngagementDailyPointSchema>;

const GuestEngagementSummarySchema = z.object({
  totalNewGuests: z.number(),
  totalReturningGuests: z.number(),
  repeatRate: z.number(),
  loyaltyOrders: z.number(),
  loyaltyParticipationRate: z.number(),
  loyaltyPointsAwarded: z.number(),
  loyaltyPointsRedeemed: z.number(),
  anonymousOrders: z.number(),
  anonymousRevenueCents: z.number(),
});
export type GuestEngagementSummary = z.infer<typeof GuestEngagementSummarySchema>;

const GuestEngagementMarketingEntrySchema = z.object({
  channel: z.string(),
  orders: z.number(),
  revenueCents: z.number(),
  newGuests: z.number(),
});
export type GuestEngagementMarketingEntry = z.infer<typeof GuestEngagementMarketingEntrySchema>;

const GuestEngagementMetricsSchema = z.object({
  daily: z.array(GuestEngagementDailyPointSchema),
  summary: GuestEngagementSummarySchema,
  marketing: z.array(GuestEngagementMarketingEntrySchema),
});
export type GuestEngagementMetrics = z.infer<typeof GuestEngagementMetricsSchema>;

const ChannelFulfillmentEntrySchema = z.object({
  key: z.enum(['onPremise', 'marketplaceDelivery']),
  orders: z.number(),
  revenueCents: z.number(),
  averageOrderValueCents: z.number(),
  slaEvaluatedOrders: z.number(),
  slaBreaches: z.number(),
  slaBreachRate: z.number(),
  slaOnTimeRate: z.number(),
  slaAverageDelaySeconds: z.number(),
});
export type ChannelFulfillmentEntry = z.infer<typeof ChannelFulfillmentEntrySchema>;

const DashboardAnalyticsSchema = z.object({
  generatedAt: z.string(),
  range: z.object({ from: z.string(), to: z.string(), days: z.number() }).passthrough(),
  qrScanTrend: z.array(QrTrendPointSchema),
  aovTrend: z.array(AovTrendPointSchema),
  abandonRate: z.object({
    totalOrders: z.number(),
    abandonedOrders: z.number(),
    abandonRate: z.number(),
  }),
  itemHeatmap: z.array(ItemHeatmapEntrySchema),
  prepSla: z.object({ evaluatedOrders: z.number(), breaches: z.number(), breachRate: z.number() }),
  churn: z.object({ activeCustomers: z.number(), retainedCustomers: z.number(), churnRate: z.number() }),
  cardSurchargeCents: z.number(),
  channelFulfillment: z.array(ChannelFulfillmentEntrySchema),
  guestEngagement: GuestEngagementMetricsSchema,
});
export type DashboardAnalytics = z.infer<typeof DashboardAnalyticsSchema>;

const DashboardAnalyticsResponseSchema = z.object({
  ok: z.literal(true),
  analytics: DashboardAnalyticsSchema,
});

export type CrossTenantSlaStatus = 'healthy' | 'warning' | 'critical';

const CrossTenantPerformanceSummarySchema = z.object({
  tenantCount: z.number(),
  activeIntegrations: z.number(),
  orders24h: z.number(),
  averageOrderValueCents: z.number().nullable(),
  revenue24hCents: z.number(),
  slaBreaches: z.number(),
});
export type CrossTenantPerformanceSummary = z.infer<typeof CrossTenantPerformanceSummarySchema>;

const CrossTenantPerformanceTenantSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  slug: z.string(),
  orders24h: z.number(),
  orders7d: z.number(),
  revenue24hCents: z.number(),
  activeIntegrations: z.number(),
  slaStatus: z.enum(['healthy', 'warning', 'critical']),
});
export type CrossTenantPerformanceTenant = z.infer<typeof CrossTenantPerformanceTenantSchema>;

const CrossTenantPerformanceAlertSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  severity: z.enum(['warning', 'critical']),
  message: z.string(),
});
export type CrossTenantPerformanceAlert = z.infer<typeof CrossTenantPerformanceAlertSchema>;

const ExternalDashboardLinkSchema = z.object({
  provider: z.enum(['looker', 'grafana']),
  label: z.string(),
  url: z.string(),
});
export type ExternalDashboardLink = z.infer<typeof ExternalDashboardLinkSchema>;

const CrossTenantPerformanceSchema = z.object({
  summary: CrossTenantPerformanceSummarySchema,
  tenants: z.array(CrossTenantPerformanceTenantSchema),
  alerts: z.array(CrossTenantPerformanceAlertSchema),
  externalDashboards: z.array(ExternalDashboardLinkSchema),
});
export type CrossTenantPerformance = z.infer<typeof CrossTenantPerformanceSchema>;

const CrossTenantPerformanceResponseSchema = z.object({
  ok: z.literal(true),
  performance: CrossTenantPerformanceSchema,
});

export const fetchAnalyticsDashboard = async (
  tenantSlug: string,
  options: { signal?: AbortSignal; days?: number } = {},
): Promise<DashboardAnalytics> => {
  const query = new URLSearchParams();
  if (options.days) {
    query.set('days', String(options.days));
  }
  const path = query.size > 0 ? `/api/analytics/dashboard?${query.toString()}` : '/api/analytics/dashboard';

  const payload = await requestJson(path, DashboardAnalyticsResponseSchema, {
    tenantSlug,
    signal: options.signal,
  });
  return payload.analytics;
};

export interface GuestEngagementDailyPoint {
  day: string;
  newGuests: number;
  returningGuests: number;
  loyaltyOrders: number;
  loyaltyPointsAwarded: number;
  loyaltyPointsRedeemed: number;
}

export type AccountingExportType = 'accounting' | 'tax';

export interface AccountingExportParams {
  type: AccountingExportType;
  from?: Date;
  to?: Date;
}

export const requestAccountingExport = async (
  tenantSlug: string,
  params: AccountingExportParams,
  options: { signal?: AbortSignal } = {},
): Promise<{ blob: Blob; filename: string }> => {
  const query = new URLSearchParams();
  query.set('type', params.type);
  if (params.from) {
    query.set('from', params.from.toISOString());
  }
  if (params.to) {
    query.set('to', params.to.toISOString());
  }

  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(`/api/analytics/accounting/export?${query.toString()}`, {
    method: 'GET',
    signal: options.signal,
    headers: {
      Accept: 'text/csv',
      'X-Tenant': tenantSlug,
    },
  });

  await assertOk(response);

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match ? match[1] : `${params.type}-export.csv`;

  return { blob, filename };
};

export const fetchCrossTenantPerformance = async (
  tenantSlug: string,
  options: { signal?: AbortSignal } = {},
): Promise<CrossTenantPerformance> => {
  const payload = await requestJson('/api/analytics/cross-tenant', CrossTenantPerformanceResponseSchema, {
    tenantSlug,
    signal: options.signal,
  });
  return payload.performance;
};
